import PropTypes from "prop-types";
import React, { PureComponent } from "react";
import theme from "styles/theme";
import TextField from "components/form/text-field";
import Cancel from "material-ui/svg-icons/navigation/cancel";
import Globalize from "globalize";
import debounce from "lodash/debounce";
import { format } from "lib/globalize-util";
import Companies from "collections/companies";
import { List, ListItem } from "material-ui/List";
import { Form } from "formsy-react";
import { css } from "glamor";
import _ from "lodash";
import InfiniteListView from "components/infinite-listview/infinite-list-view";
import Button from "components/form/button";
import { toDataTest } from "helpers/test";
import { DATATEST } from "constants/integration";
import { hasBrandCompanyAccess } from "helpers/access-control";
import me from "models/me";
import { Search } from "@material-ui/icons";

const styles = {
    holder: {
        position: "relative",
    },
    inputStyle: {
        padding: "0 36px 0 16px",
        marginTop: "-1px",
    },
    searchTextContainer: {
        height: "40px",
    },
    hintStyle: {
        padding: "0 0 0 16px",
        bottom: "1px",
        lineHeight: "40px",
        height: "40px",
    },
    noResults: {
        fontSize: "14px",
        padding: "0 24px 10px",
    },
    cancel: {
        position: "absolute",
        top: "50%",
        right: "12px",
        transform: "translateY(-50%)",
        zIndex: 2,
        fill: theme.palette.grey,
        transition: "all 0.3s ease",
        opacity: 0,
    },
    cancelShow: {
        opacity: 1,
        cursor: "pointer",
    },
    cancelPopup: {
        top: "auto",
        bottom: "0",
        right: "0",
        opacity: 1,
        cursor: "pointer",
        transform: "translateY(0)",
    },
    listItem: {
        padding: "0px 12px",
    },
    listItemText: {
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        width: "100%",
        overflow: "hidden",
        lineHeight: "18px",
        fontSize: "14px",
    },
    selectedListItemText: {
        color: theme.palette.primary1Color,
    },
    disabledListItemText: {
        color: theme.palette.grey,
    },
    list: {
        display: "block",
        maxHeight: "250px",
        overflow: "auto",
    },
    actionsBar: {
        textAlign: "right",
    },
};

const stylesB2bPhase1 = {
    holder: {
        position: "relative",
        margin: "20px 16px 0px",
    },
    inputStyle: {
        padding: "0 36px 0 40px",
        marginTop: "-1px",
    },
    hintStyle: {
        padding: "0 0 0 15px",
        bottom: "1px",
        lineHeight: "40px",
        height: "40px",
        marginLeft: "25px",
    },
    emptyHeader: {
        color: theme.palette.textColor,
        fontWeight: 700,
        margin: "0 0 20px",
        fontSize: "14px",
    },
    emptyContent: {
        fontWeight: 400,
    },
    searchIcon: {
        color: theme.palette.placeholderText,
        position: "absolute",
        fontSize: "22px !important",
        left: "14px",
        top: "9px",
    },
    searchIconActive: {
        color: theme.palette.darkGrey,
    },
};

class CompanyRow extends PureComponent {
    render() {
        const { model: company, isSelected, onAction, childComponentProps = {} } = this.props;
        const { scope } = childComponentProps;
        const useCompanyAccessControl = scope?.brand?.hasPreference("company_access_control");
        const isBuyer = me.isBuyer();
        const hasAccess = !isBuyer || hasBrandCompanyAccess({ useCompanyAccessControl, company });
        const txtComponent = (
            <div
                {...css(
                    styles.listItemText,
                    isSelected && styles.selectedListItemText,
                    !hasAccess && styles.disabledListItemText
                )}
            >
                {company.__name}
            </div>
        );
        return (
            <ListItem
                data-test={`company-${toDataTest(company.__name)}`}
                {...css(styles.listItem)}
                key={company._id}
                primaryText={txtComponent}
                title={company.__name}
                disabled={!hasAccess}
                onClick={onAction(company)}
            />
        );
    }
}

class SearchableCompanyInfiniteListView extends PureComponent {
    static propTypes = {
        scope: PropTypes.object.isRequired,
        showSearch: PropTypes.bool.isRequired,
        onSelect: PropTypes.func.isRequired,
        selectedCompany: PropTypes.string,
        onCancel: PropTypes.func,
        watermarking: PropTypes.object,
    };

    constructor(props) {
        super(props);

        this.state = {
            searchTerm: "", // buffered value
            textFieldValue: "", // current value
            companies: new Companies(),
            shouldShowLatestCompanies: true,
        };

        this.handleTextFieldChange = this.handleTextFieldChange.bind(this);
        this.handleSearchChange = debounce(this.handleSearchChange.bind(this), 500);
    }

    handleTextFieldChange(e, value) {
        this.setState({ textFieldValue: value });
        this.handleSearchChange(value);
    }

    handleSearchChange(searchTerm) {
        this.setState({ searchTerm: searchTerm });
        this.handleRecentlyAddedCompanies(searchTerm);
    }

    handleRecentlyAddedCompanies(searchTerm) {
        this.setState({ shouldShowLatestCompanies: searchTerm?.trim() === "" });
    }

    render() {
        const { scope, showSearch, selectedCompany, onCancel, latestCompanies, b2bPhase1 } = this.props;
        const { searchTerm, textFieldValue, companies, shouldShowLatestCompanies } = this.state;
        const emptyResultsComponent = (
            <div {...css(styles.noResults)}>{Globalize.formatMessage("No results found")}</div>
        );
        const useCompanyAccessControl = scope?.brand?.hasPreference("company_access_control");

        const emptyCollectionComponent = (
            <>
                <p {...css(stylesB2bPhase1.emptyHeader)}>{Globalize.formatMessage("No companies set up")}</p>
                <p {...css(stylesB2bPhase1.emptyHeader, stylesB2bPhase1.emptyContent)}>
                    {Globalize.formatMessage("Add a company to your ERP system so they can place orders.")}
                </p>
            </>
        );

        let list;
        if (showSearch) {
            const searchField = b2bPhase1 === true ? "name" : undefined;
            list = (
                <InfiniteListView
                    scope={scope}
                    componentType="company"
                    searchTerm={searchTerm}
                    searchField={searchField}
                    fields={this.getFields()}
                    collection={companies}
                    emptyResultsComponent={emptyResultsComponent}
                    pageSize={10}
                    childComponent={CompanyRow}
                    childHeight={b2bPhase1 ? 40 : 48}
                    onAction={this.handleSelect}
                    height={b2bPhase1 ? 349 : 240}
                    selectedCompany={selectedCompany}
                    childComponentProps={{ scope }}
                    shouldShowPreDisplayedComponents={shouldShowLatestCompanies}
                    preDisplayedComponentRows={latestCompanies}
                    preDisplayedUnclickableComponentHeading={format("Recently added companies")}
                    shouldDisplayDividingSeparator={true}
                    collectionsHeading={format("All companies")}
                    emptyCollectionComponent={b2bPhase1 && emptyCollectionComponent}
                    b2bPhase1={b2bPhase1}
                />
            );
        } else {
            list = (
                <List {...css(styles.list)}>
                    {scope.companies.map((company) => {
                        const hasAccess = !me.isBuyer() || hasBrandCompanyAccess({ useCompanyAccessControl, company });
                        return (
                            <ListItem
                                key={company._id}
                                disabled={!hasAccess}
                                style={{
                                    ...(selectedCompany === company._id ? styles.selectedListItemText : {}),
                                    ...(hasAccess ? {} : styles.disabledListItemText),
                                }}
                                primaryText={company.__name}
                                onClick={this.handleSelect(company)}
                            />
                        );
                    })}
                </List>
            );
        }

        return (
            <div>
                <Form>
                    {showSearch && (
                        <div {...css(b2bPhase1 ? stylesB2bPhase1.holder : styles.holder)}>
                            <TextField
                                autoFocus={true}
                                hintText={Globalize.formatMessage("Search companies")}
                                adornment={
                                    b2bPhase1 && (
                                        <Search
                                            {...css(
                                                stylesB2bPhase1.searchIcon,
                                                textFieldValue && stylesB2bPhase1.searchIconActive
                                            )}
                                        />
                                    )
                                }
                                large={false}
                                onChange={this.handleTextFieldChange}
                                value={textFieldValue}
                                name="company-search"
                                fullWidth="190px"
                                inputStyle={b2bPhase1 ? stylesB2bPhase1.inputStyle : styles.inputStyle}
                                hintStyle={b2bPhase1 ? stylesB2bPhase1.hintStyle : styles.hintStyle}
                                style={styles.searchTextContainer}
                                allowWhitespace={true}
                                dataTest={DATATEST.PREFERRED_RETAILERS.COMPANY_SEARCH}
                            />
                            <Cancel
                                onClick={this.handleClearSearch}
                                color="#999"
                                {...css(Object.assign({}, styles.cancel, textFieldValue && styles.cancelShow))}
                            />
                        </div>
                    )}
                </Form>
                <div data-test={DATATEST.PREFERRED_RETAILERS.COMPANY_NAME} style={{ display: "block" }}>
                    {list}
                </div>
                <div {...css(styles.actionsBar)}>
                    {onCancel && <Button label={Globalize.formatMessage("Close")} onClick={onCancel} />}
                </div>
            </div>
        );
    }

    handleSelect = (company) => (event) => {
        event.preventDefault();
        this.props.onSelect(company);
        this.handleClearSearch();
    };

    handleClearSearch = () => {
        this.setState({ textFieldValue: "", searchTerm: "", shouldShowLatestCompanies: true });
    };

    getFields() {
        const base = [
            "_id",
            "__name",
            "__schema_id",
            "__currency_codes",
            "__settings.__pricing_template",
            "__settings.__default_discount",
            "__merchant_id",
        ];
        const access = this.props.scope?.brand?.hasPreference("company_access_control") ? ["access_control"] : [];
        const roles = this.props.scope?.brand?.hasPreference("allow_custom_roles") ? ["__buyers"] : [];
        const watermarkAttributeFields = this.props.scope?.brand?.hasPreference("add_watermarking")
            ? _.map(this.props.watermarking?.dataAttributes, "key")
            : [];
        return [...base, ...access, ...roles, ...watermarkAttributeFields].join();
    }
}

export default SearchableCompanyInfiniteListView;
